import React, { useEffect } from "react";
import "~/css/ChatPage.css";
import "~/App.css";
import "~/css/MainContents.css";
import "~/css/SideBar.css";
import ChatHistory from "./ChatHistory";
import ControlButton from "~/components/ControlButton";
import ModelView from "~/components/ModelView";
import SideBar from "~/components/SideBar";
import { useAtom } from "jotai/index";
import {
  currentVRMAtom,
  isSettingOpenAtom,
  isStandbyModeAtom,
  languageAtom,
  standbyModeAtom,
  T2SEngineAtom,
  volumeAtom,
  layoutSelectAtom,
} from "~/Atom";
import { PlaySound } from "~/SoundUtils";
import Footer from "~/components/Footer";
import * as db from "~/components/db/CharacterDBUtils";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import StandbyScreen from "~/components/modal/StandbyScreen";
import { t2SEngines, currentLocale, talkModes } from "~/Commons";
import Header from "~/components/Header";
import ChatPageLayout1 from "~/components/layout/ChatPageLayout1";
import ChatPageLayout2 from "~/components/layout/ChatPageLayout2";
import ChatPageLayout3 from "~/components/layout/ChatPageLayout3";

// 音声関連の定数
const standbyExitVolumeScale = 0.5; // スタンバイ終了時の音量スケール

const ChatPage: React.FC = () => {
  const [volume] = useAtom(volumeAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const [standbyMode, setStandbyMode] = useAtom(standbyModeAtom);
  const [language] = useAtom(languageAtom);
  const [isStandbymode] = useAtom(isStandbyModeAtom);
  const [isSettingOpen] = useAtom(isSettingOpenAtom);
  const [layoutSelect] = useAtom(layoutSelectAtom);

  const closeStandbyScreen = () => {
    setStandbyMode(talkModes.active);
    PlaySound("speak", standbyExitVolumeScale * volume);
  };

  const updateT2SEngine = async () => {
    try {
      // currentVRMが空の場合は処理をスキップする
      if (!currentVRM) return;

      const currentCharacter = getVRMName(currentVRM);
      const characterData = await db.selectByName(currentCharacter);

      if (!characterData || !characterData.tts_engine) {
        setT2SEngine(t2SEngines.openai);
        return;
      }

      if (language === currentLocale) {
        setT2SEngine(characterData.tts_engine);
      } else {
        setT2SEngine(t2SEngines.openai);
      }
    } catch {
      setT2SEngine(t2SEngines.openai);
    }
  };

  useEffect(() => {
    updateT2SEngine();
  }, [currentVRM, language]);

  /**
   * ブラウザバックを検知したらリロード
   * ブラウザバックによる状態を初期化してモデルの読み込みが正常に行われない問題の回避
   */
  useEffect(() => {
    const handlePageReload = () => {
      window.location.reload();
    };
    window.addEventListener("popstate", handlePageReload);
    window.addEventListener("hashchange", handlePageReload);
    setStandbyMode(talkModes.standby);

    return () => {
      window.removeEventListener("popstate", handlePageReload);
      window.removeEventListener("hashchange", handlePageReload);
    };
  }, []);

  const content = () => {
    switch (layoutSelect) {
      case 1:
        return <ChatPageLayout1 isSettingOpen={isSettingOpen} isStandbymode={isStandbymode} />;
      case 2:
        return <ChatPageLayout2 isSettingOpen={isSettingOpen} isStandbymode={isStandbymode} />;
      case 3:
        return <ChatPageLayout3 isSettingOpen={isSettingOpen} isStandbymode={isStandbymode} />;
      default:
        return (
          <>
            <Header />
            <div className={`main-contents ${isSettingOpen ? "open" : ""}`}>
              <ModelView visible={!isStandbymode} />
              <div className="sub-contents">
                <div className="chat-space">
                  <div className="chat-container">
                    <ChatHistory />
                  </div>
                </div>
                <ControlButton />
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="App-page">
      <StandbyScreen
        isOpen={standbyMode === talkModes.standby}
        onClose={() => closeStandbyScreen()}
      />
      {content()}
      <SideBar />
      <Footer />
    </div>
  );
};

export default ChatPage;
